import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trophy, TrendingUp, TrendingDown, Users, BarChart3, Mail,
  Loader2, RefreshCw, ArrowRight, History, CreditCard, Settings,
  Target, Lightbulb, Star, ChevronRight, Sparkles, AlertCircle, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { SupportButton } from '../components/SupportWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoreBadge({ score }) {
  const color = score >= 70
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : score >= 40
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-sm font-semibold rounded-full border ${color}`} data-testid="score-badge">
      {score}
    </span>
  );
}

function LeaderboardRow({ rep, rank, insight }) {
  const [expanded, setExpanded] = useState(false);
  const rankDisplay = rank <= 3
    ? ['', '1st', '2nd', '3rd'][rank]
    : `#${rank}`;
  const rankStyle = rank === 1
    ? 'bg-amber-400 text-white'
    : rank === 2
      ? 'bg-zinc-300 text-zinc-700'
      : rank === 3
        ? 'bg-amber-600 text-white'
        : 'bg-zinc-100 text-zinc-500';

  return (
    <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden transition-all" data-testid={`leaderboard-row-${rep.user_id}`}>
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-zinc-50/50 transition-colors"
        onClick={() => insight && setExpanded(!expanded)}
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${rankStyle}`}>
          {rankDisplay}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-zinc-900 truncate">{rep.name}</span>
            {rank === 1 && <Trophy className="w-4 h-4 text-amber-500" />}
          </div>
          <span className="text-xs text-zinc-500">{rep.email}</span>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <div className="text-xs text-zinc-400 uppercase tracking-wide">Calls</div>
            <div className="text-sm font-semibold text-zinc-700">{rep.total_calls}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-zinc-400 uppercase tracking-wide">Avg Score</div>
            <ScoreBadge score={rep.avg_score} />
          </div>
          {insight && (
            <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          )}
        </div>
      </div>

      {expanded && insight && (
        <div className="border-t border-zinc-100 bg-zinc-50/50 p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`insight-panel-${rep.user_id}`}>
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Target className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Coaching Areas</span>
            </div>
            <ul className="space-y-1.5">
              {(insight.coaching_areas || []).map((area, i) => (
                <li key={i} className="text-sm text-zinc-600 flex items-start gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                  {area}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Star className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Praise Areas</span>
            </div>
            <ul className="space-y-1.5">
              {(insight.praise_areas || []).map((area, i) => (
                <li key={i} className="text-sm text-zinc-600 flex items-start gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                  {area}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb className="w-3.5 h-3.5 text-brand-primary" />
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-primary">Next Call Tip</span>
            </div>
            <p className="text-sm text-zinc-600 bg-brand-muted border border-brand-accent-muted rounded-md p-2.5">
              {insight.tip || 'Keep up the great work!'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryBreakdown({ leaderboard }) {
  // Collect all categories across all reps
  const allCategories = {};
  leaderboard.forEach(rep => {
    Object.entries(rep.category_averages || {}).forEach(([cat, score]) => {
      if (!allCategories[cat]) allCategories[cat] = [];
      allCategories[cat].push(score);
    });
  });

  const catAvgs = Object.entries(allCategories).map(([cat, scores]) => ({
    category: cat,
    avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
  })).sort((a, b) => b.avg - a.avg);

  if (catAvgs.length === 0) return null;

  return (
    <Card className="border-zinc-200" data-testid="category-breakdown">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Team Category Performance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {catAvgs.map(({ category, avg }) => (
          <div key={category} className="flex items-center gap-3">
            <span className="text-sm text-zinc-600 w-36 truncate">{category}</span>
            <div className="flex-1 bg-zinc-100 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  avg >= 70 ? 'bg-emerald-500' : avg >= 40 ? 'bg-amber-400' : 'bg-red-400'
                }`}
                style={{ width: `${avg}%` }}
              />
            </div>
            <span className="text-sm font-medium text-zinc-700 w-10 text-right">{avg}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ManagerDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [days, setDays] = useState(30);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await axios.get(`${API}/analytics/manager-insights?days=${days}`);
      setData(result);
    } catch (e) {
      console.error('Failed to fetch manager insights:', e);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data: result } = await axios.post(`${API}/analytics/send-weekly-digest`, {});
      if (result.sent) {
        toast.success('Weekly digest sent to your email!');
      } else {
        toast.info(result.reason || 'No data to send');
      }
    } catch (e) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const previewDigest = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewHtml(null);
    try {
      const { data: result } = await axios.get(`${API}/analytics/digest-preview?days=${days}`);
      if (result.has_data) {
        setPreviewHtml(result.html);
      } else {
        setPreviewOpen(false);
        toast.info(result.reason || 'No data to preview');
      }
    } catch (e) {
      setPreviewOpen(false);
      toast.error(e.response?.data?.detail || 'Failed to build digest preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const totalCalls = data?.leaderboard?.reduce((s, r) => s + r.total_calls, 0) || 0;
  const teamAvg = data?.leaderboard?.length
    ? Math.round(data.leaderboard.reduce((s, r) => s + r.avg_score, 0) / data.leaderboard.length)
    : 0;
  const topPerformer = data?.leaderboard?.[0];

  return (
    <div className="min-h-screen bg-white" data-testid="manager-dashboard">
      {/* Header */}
      <header className="h-16 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-3">
          <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-8 w-8 object-contain" />
          <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>PipeGuide</span>
          <Badge variant="outline" className="ml-2 text-xs text-brand-primary border-brand-accent bg-brand-muted">Manager</Badge>
        </div>

        <nav className="flex items-center gap-1">
          <Link to="/manager">
            <Button variant="ghost" size="sm" className="text-brand-primary bg-brand-muted rounded-md" data-testid="nav-insights">
              <Sparkles className="w-4 h-4 mr-1.5" /> Insights
            </Button>
          </Link>
          <Link to="/team">
            <Button variant="ghost" size="sm" className="rounded-md" data-testid="nav-team">
              <Users className="w-4 h-4 mr-1.5" /> Team
            </Button>
          </Link>
          <Link to="/history">
            <Button variant="ghost" size="sm" className="rounded-md" data-testid="nav-history">
              <History className="w-4 h-4 mr-1.5" /> History
            </Button>
          </Link>
          <Link to="/billing">
            <Button variant="ghost" size="sm" className="rounded-md" data-testid="nav-billing">
              <CreditCard className="w-4 h-4 mr-1.5" /> Billing
            </Button>
          </Link>
          <div className="w-px h-6 bg-zinc-200 mx-2" />
          <SupportButton />
          <Button variant="ghost" size="sm" onClick={logout} className="rounded-md text-zinc-500" data-testid="nav-logout">
            Sign Out
          </Button>
        </nav>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Title row */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }} data-testid="dashboard-title">
              Team Insights
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              AI-powered coaching dashboard for your sales team
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border border-zinc-200 rounded-md px-3 py-1.5 bg-white text-zinc-700"
              data-testid="days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={fetchInsights} disabled={loading} className="rounded-md" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={previewDigest} disabled={previewLoading || loading} className="rounded-md" data-testid="preview-digest-btn">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
              Preview Digest
            </Button>
            <Button variant="outline" size="sm" onClick={sendDigest} disabled={sendingDigest || loading} className="rounded-md" data-testid="send-digest-btn">
              {sendingDigest ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
              Send Digest
            </Button>
          </div>
        </div>

        {/* Weekly digest email preview */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="digest-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Weekly Digest Preview</DialogTitle>
              <DialogDescription>
                This is the exact coaching email managers receive every week — built from the last {days} days of team calls.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 min-h-[60vh]">
              {previewLoading || !previewHtml ? (
                <div className="flex flex-col items-center justify-center h-full py-24" data-testid="digest-preview-loading">
                  <Loader2 className="w-7 h-7 text-brand-primary animate-spin mb-3" />
                  <p className="text-sm text-zinc-500">Generating AI coaching digest…</p>
                </div>
              ) : (
                <iframe
                  title="Weekly Digest Preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-[60vh] border-0"
                  data-testid="digest-preview-frame"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24" data-testid="loading-state">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mb-4" />
            <p className="text-zinc-500">Generating AI insights for your team...</p>
          </div>
        ) : !data?.leaderboard?.length ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
            <Users className="w-12 h-12 text-zinc-300 mb-4" />
            <h2 className="text-lg font-semibold text-zinc-700 mb-1">No Team Data Yet</h2>
            <p className="text-sm text-zinc-500 max-w-md">
              {data?.team_summary || 'Once your sales reps start making calls, their performance insights will appear here.'}
            </p>
            <Link to="/team" className="mt-4">
              <Button variant="outline" size="sm" className="rounded-md">
                <Users className="w-4 h-4 mr-1.5" /> Manage Team
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stat-cards">
              <Card className="border-zinc-200">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">Team Members</div>
                  <div className="text-2xl font-bold text-zinc-900">{data.leaderboard.length}</div>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">Total Calls</div>
                  <div className="text-2xl font-bold text-zinc-900">{totalCalls}</div>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">Team Avg Score</div>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold text-zinc-900">{teamAvg}</div>
                    {teamAvg >= 60 ? (
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">Top Performer</div>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <span className="text-lg font-bold text-zinc-900 truncate">{topPerformer?.name || '-'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Team Summary */}
            {data.team_summary && (
              <Card className="border-brand-accent bg-brand-muted/50" data-testid="team-summary">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-brand-primary mb-1">AI Team Overview</div>
                      <p className="text-sm text-brand-primary leading-relaxed">{data.team_summary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main content: Leaderboard + Category breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3" data-testid="leaderboard">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4" />
                  Leaderboard
                  <span className="text-xs font-normal text-zinc-400 ml-1">(Click a row to see AI coaching)</span>
                </h2>
                {data.leaderboard.map((rep, i) => (
                  <LeaderboardRow
                    key={rep.user_id}
                    rep={rep}
                    rank={i + 1}
                    insight={data.insights?.[rep.name]}
                  />
                ))}
              </div>
              <div className="space-y-4">
                <CategoryBreakdown leaderboard={data.leaderboard} />

                {data.generated_at && (
                  <p className="text-xs text-zinc-400 text-center">
                    Last generated: {new Date(data.generated_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
